import { Repetition, Unit, WorkingTimeClassicType } from "../../__generated__/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormatWorkingTimeClassicSpec, useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";

import SelectField from "../SelectField";
import {
    MAX_DAILY_HOURS,
    maxNbDays,
    useHasErrorWorkingTimeClassicSpec,
    WorkingTimeComponentProps,
} from "./helpers";
import TextField from "../TextField";
import { BodyMediumText, BodySmallText } from "../FormattedText";
import Gap from "../../ui_atoms/Gap";
import { Buildable } from "ts-essentials";
import { useTheme } from "../../hooks/use-theme";
import { SelectItemType } from "../select/SelectChooser";
import { DEFAULT_HOURS_PER_WEEK } from "../../utils";
import OptionsField from "../OptionsField";

const DailyClassicComponent: React.FC<WorkingTimeComponentProps> = ({
    disabled = false,
    contract,
    setContract,
}) => {
    const { colors } = useTheme();
    const i18n = useI18n();
    const workingTime = contract.workingTimeClassicSpec;
    const repetition = contract.workingTimeClassicSpec?.repetition;
    const [isRepetition, setIsRepetition] = useState(repetition !== Repetition.None);
    const [unitCount, setUnitCount] = useState(workingTime?.unitCount?.toString() || "");
    const [dailyHours, setDailyHours] = useState(workingTime?.dailyHours?.toString() || "");
    const setter = useCallback(
        (v: { workingTime: Buildable<WorkingTimeClassicType> }) =>
            setContract({
                ...contract,
                workingTimeClassicSpec: v.workingTime,
            }),
        [setContract, contract],
    );
    const hasErrorWorkingTimeClassicSpec = useHasErrorWorkingTimeClassicSpec();

    const { errorUnitCount, errorDailyHours } = useMemo(() => {
        const hoursPerWeek = contract.hoursPerWeekFullTime || DEFAULT_HOURS_PER_WEEK;
        return hasErrorWorkingTimeClassicSpec(
            contract.workingTimeClassicSpec?.unit || Unit.Hours,
            repetition,
            Number(unitCount),
            hoursPerWeek,
            Number(dailyHours),
        );
    }, [
        hasErrorWorkingTimeClassicSpec,
        contract.hoursPerWeekFullTime,
        repetition,
        unitCount,
        dailyHours,
        contract.workingTimeClassicSpec?.unit,
    ]);

    const changeWorkingTime = useCallback(
        (unitCount: string, dailyHours: string, repetition?: Repetition) => {
            let unitCountAsNumber: number | undefined = Number(unitCount.replace(",", "."));
            let dailyHoursAsNumber: number | undefined = Number(dailyHours.replace(",", "."));
            if (
                isNaN(unitCountAsNumber) ||
                unitCountAsNumber <= 0 ||
                unitCountAsNumber > maxNbDays(repetition)
            ) {
                unitCountAsNumber = undefined;
            }
            if (
                isNaN(dailyHoursAsNumber) ||
                dailyHoursAsNumber <= 0 ||
                dailyHoursAsNumber > MAX_DAILY_HOURS
            ) {
                dailyHoursAsNumber = undefined;
            }
            setter({
                workingTime: {
                    repetition,
                    unit: Unit.Days,
                    unitCount: unitCountAsNumber,
                    dailyHours: dailyHoursAsNumber,
                },
            });
        },
        [setter],
    );

    const onRepetitionChange = useCallback(
        (value: boolean) => {
            setIsRepetition(value);
            if (!value) {
                changeWorkingTime(unitCount, dailyHours, Repetition.None);
            } else {
                changeWorkingTime(unitCount, dailyHours, undefined);
            }
        },
        [changeWorkingTime, dailyHours, unitCount],
    );

    const onUnitCountChange = useCallback(
        (value: string) => {
            setUnitCount(value);
            changeWorkingTime(value, dailyHours, repetition);
        },
        [repetition, dailyHours, changeWorkingTime],
    );
    const onDailyHoursChange = useCallback(
        (value: string) => {
            setDailyHours(value);
            changeWorkingTime(unitCount, value, repetition);
        },
        [repetition, unitCount, changeWorkingTime],
    );
    const onRepetitionChanges = useCallback(
        ({ value }: SelectItemType<Repetition>) => {
            changeWorkingTime(unitCount, dailyHours, value);
        },
        [unitCount, dailyHours, changeWorkingTime],
    );

    useEffect(() => {
        if (!contract.workingTimeClassicSpec) {
            setContract({
                ...contract,
                workingTimeClassicSpec: {
                    repetition: Repetition.Week,
                    unitCount: undefined,
                    unit: Unit.Days,
                },
            });
        }
    }, [contract, setContract]);

    useEffect(() => {
        if (workingTime?.unitCount !== undefined) {
            setUnitCount(workingTime?.unitCount?.toString() || "");
        }
    }, [workingTime?.unitCount]);

    const repetitionItems = useMemo(() => {
        return [
            {
                label: i18n.t("utils.working_time.repetition_for_daily.week"),
                value: Repetition.Week,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_daily.week2"),
                value: Repetition.Week2,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_daily.week3"),
                value: Repetition.Week3,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_daily.week4"),
                value: Repetition.Week4,
            },
            {
                label: i18n.t("utils.working_time.repetition_for_daily.month"),
                value: Repetition.Month,
            },
        ];
    }, [i18n]);
    const formatWorkingTime = useFormatWorkingTimeClassicSpec();

    if (!contract.workingTimeClassicSpec) {
        return null;
    }
    return (
        <VStack v="1/3" gap={0.5} style={{ paddingTop: 0 }}>
            <OptionsField<boolean>
                value={isRepetition}
                onSelect={onRepetitionChange}
                data={[
                    { label: i18n.t("utils.working_time.recurrent"), value: true },
                    { label: i18n.t("utils.working_time.punctual"), value: false },
                ]}
            />
            {isRepetition && (
                <>
                    <Gap gap={0.5} />
                    <BodyMediumText>
                        {i18n.t("utils.working_time.repetition_info_daily")}
                    </BodyMediumText>
                    <SelectField
                        onSelect={onRepetitionChanges}
                        placeholder={i18n.t("utils.working_time.repetition_label_daily")}
                        data={repetitionItems}
                        value={repetition || undefined}
                        halfScreen
                    />
                </>
            )}
            <Gap gap={0.1} />
            <BodyMediumText>
                {i18n.t(`utils.working_time.days_info.${repetition || Repetition.None}`)}
            </BodyMediumText>
            <TextField
                selectTextOnFocus
                inputStyle={{
                    textAlign: "right",
                }}
                disabled={disabled}
                value={unitCount}
                isDecimalNumber
                onChangeText={onUnitCountChange}
                returnKeyType="done"
                error={unitCount ? errorUnitCount : undefined}
            />
            {workingTime?.unitCount && workingTime?.repetition && (
                <BodySmallText color={colors.fieldLabel}>
                    {formatWorkingTime(workingTime)}
                </BodySmallText>
            )}
            <Gap gap={0.1} />
            <BodyMediumText>{i18n.t("utils.working_time.daily_hours_question")}</BodyMediumText>
            <TextField
                selectTextOnFocus
                inputStyle={{
                    textAlign: "right",
                }}
                disabled={disabled}
                value={dailyHours}
                isDecimalNumber
                onChangeText={onDailyHoursChange}
                returnKeyType="done"
                error={dailyHours ? errorDailyHours : undefined}
            />
            {workingTime?.dailyHours && (
                <BodySmallText color={colors.fieldLabel}>
                    {i18n.t("utils.working_time.daily_hours", {
                        count: workingTime?.dailyHours,
                    })}
                </BodySmallText>
            )}
        </VStack>
    );
};

export default DailyClassicComponent;
